import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { SuggestedTaskItem, TaskItem, TaskItemDuration } from '@demind-inc/core';

import {
  DEFAULT_TASK_DURATION_MIN,
  generalSnackBarAtom,
  taskTimeSuggestionMapAtom,
  useAuthContext,
  useSuggestIndividualTaskTime,
  transformHHmmToTargetDate,
} from '../data-access';
import { TemporaryTask } from '../data-access/api';
import { trackEventMixpanel } from '../utils';

type Params = {
  date: string;
  task: Partial<TaskItem>;
  suggestionKey?: string;
};

let suggestionQueue: Promise<unknown> = Promise.resolve();

interface QueuedTaskInfo {
  key: string;
  taskName?: string;
  duration: TaskItemDuration;
}

const queuedTasks: QueuedTaskInfo[] = [];

const enqueueSuggestionRequest = <T>(
  handler: () => Promise<T>,
  taskInfo?: QueuedTaskInfo
): Promise<T> => {
  if (taskInfo) {
    queuedTasks.push(taskInfo);
  }
  const queued = suggestionQueue.then(handler, handler);
  suggestionQueue = queued.catch(() => undefined);
  return queued;
};

const toDuration = (duration?: TaskItemDuration): TaskItemDuration => {
  if (duration?.amount && duration?.unit) return duration;
  return { amount: DEFAULT_TASK_DURATION_MIN, unit: 'minute' };
};

export const useTaskTimeSuggestion = ({ date, task, suggestionKey }: Params) => {
  const { user } = useAuthContext();
  const { suggestIndividualTaskTime } = useSuggestIndividualTaskTime();
  const [taskSuggestionMap, setTaskSuggestionMap] = useRecoilState(taskTimeSuggestionMapAtom);
  const [, setGeneralSnackbar] = useRecoilState(generalSnackBarAtom);
  const fallbackKeyRef = useRef(`tmp:${Math.random().toString(36).slice(2)}`);
  const taskSuggestionMapRef = useRef(taskSuggestionMap);

  // Keep ref in sync with state
  taskSuggestionMapRef.current = taskSuggestionMap;

  const resolvedDate = useMemo(() => {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed : dayjs();
  }, [date]);

  const resolvedDateString = useMemo(() => resolvedDate.format('YYYY-MM-DD'), [resolvedDate]);

  const key =
    suggestionKey ??
    ((task as any)?.taskId as string | undefined) ??
    ((task as any)?.originalTaskId as string | undefined) ??
    fallbackKeyRef.current;

  const state = taskSuggestionMap[key];

  const canRequestSuggestion = useMemo(() => {
    return Boolean(user?.userId && user?.metricId && user?.preferenceId);
  }, [user?.metricId, user?.preferenceId, user?.userId]);

  const isPending = state?.status === 'pending';

  const suggestedTime: Dayjs | null = useMemo(() => {
    if (!state?.startTime) return null;
    const parsed = dayjs(state.startTime);
    return parsed.isValid() ? parsed : null;
  }, [state?.startTime]);

  const suggestionExplanation = state?.reasoning;

  const clearSuggestion = useCallback(() => {
    setTaskSuggestionMap((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [key, setTaskSuggestionMap]);

  const parseSuggestedTime = useCallback(
    (suggestedTask?: SuggestedTaskItem | null) => {
      if (!suggestedTask) return null;

      if (suggestedTask.suggestedStartTime) {
        const startDayOffset = suggestedTask.suggestedStartDayOffset ?? 0;
        const baseDay = resolvedDate.startOf('day').add(startDayOffset, 'day');
        const suggestedDate = transformHHmmToTargetDate(suggestedTask.suggestedStartTime, baseDay);
        return dayjs(suggestedDate);
      }

      const datetime = (suggestedTask as any)?.dueDateTime?.datetime;
      if (datetime) return dayjs(datetime);

      return null;
    },
    [resolvedDate]
  );

  const requestSuggestion = useCallback(
    async (callback?: (suggestedTime: Dayjs) => Promise<void> | void) => {
      if (!canRequestSuggestion || isPending) return null;

      const taskDuration = toDuration(task.duration);
      const fallbackDueDate = task?.dueDateTime ?? {};
      const taskPayload: Partial<TaskItem> = {
        ...task,
        duration: taskDuration,
        dueDateTime: {
          ...fallbackDueDate,
          date: fallbackDueDate?.date || resolvedDateString,
          timezone: fallbackDueDate?.timezone || dayjs.tz?.guess?.(),
        },
      };

      setTaskSuggestionMap((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] ?? { status: 'idle' }),
          status: 'pending',
        },
      }));

      const taskInfo: QueuedTaskInfo = {
        key,
        taskName: task.name,
        duration: taskDuration,
      };

      return enqueueSuggestionRequest(async () => {
        try {
          const now = dayjs();
          const suggestionCurrentTime = resolvedDate.isAfter(now, 'day')
            ? resolvedDate.startOf('day').set('hour', 4).toISOString() // 4am of the suggested date
            : now.toISOString();

          // Collect previous tasks from the queue that have been suggested
          // Use ref to get the latest state since we're in an async callback
          const temporaryTasks: TemporaryTask[] = queuedTasks
            .filter((queuedTask) => queuedTask.key !== key)
            .map((queuedTask) => {
              const suggestionState = taskSuggestionMapRef.current[queuedTask.key];
              if (suggestionState?.status === 'suggested' && suggestionState.startTime) {
                const startTime = dayjs(suggestionState.startTime);
                const endTime = startTime.add(queuedTask.duration.amount, queuedTask.duration.unit);
                return {
                  taskId: queuedTask.key,
                  name: queuedTask.taskName || '',
                  startTime: startTime.format('HH:mm'),
                  endTime: endTime.format('HH:mm'),
                };
              }
              return null;
            })
            .filter((task): task is TemporaryTask => task !== null);

          const result = await suggestIndividualTaskTime({
            userId: user.userId,
            metricId: user.metricId!,
            preferenceId: user.preferenceId!,
            currentTime: suggestionCurrentTime,
            task: taskPayload,
            temporaryTasks: temporaryTasks.length > 0 ? temporaryTasks : undefined,
          });

          const suggestedTask = result?.suggestedTask as SuggestedTaskItem | undefined;

          // Check if suggestedStartTime is missing
          if (!suggestedTask?.suggestedStartTime) {
            // Reset the suggestion state
            setTaskSuggestionMap((prev) => {
              if (!prev[key]) return prev;
              const next = { ...prev };
              delete next[key];
              return next;
            });

            const reasoning = suggestedTask.reasoning || result?.detailedExplanation;

            // Show snackbar with detailed explanation
            if (reasoning) {
              setGeneralSnackbar('No time found for this task: ' + reasoning);
            } else if (result.detailedExplanation) {
              setGeneralSnackbar('No time found for this task: ');
            }
            return null;
          }

          const nextSuggestedTime = parseSuggestedTime(suggestedTask);
          if (!nextSuggestedTime) return null;

          // Compute end time using suggestedEndTime + endDayOffset when available
          let endTime: Dayjs;
          if (suggestedTask?.suggestedEndTime) {
            const endDayOffset = suggestedTask.suggestedEndDayOffset ?? 0;
            const endBaseDay = resolvedDate.startOf('day').add(endDayOffset, 'day');
            const endDate = transformHHmmToTargetDate(suggestedTask.suggestedEndTime, endBaseDay);
            endTime = dayjs(endDate);
          } else {
            endTime = nextSuggestedTime.add(taskDuration.amount, taskDuration.unit);
          }

          // Call the callback if provided, then update status after it completes
          if (callback) {
            await callback(nextSuggestedTime);
          }
          setTaskSuggestionMap((prev) => ({
            ...prev,
            [key]: {
              status: 'suggested',
              startTime: nextSuggestedTime.toISOString(),
              endTime: endTime.toISOString(),
              reasoning: result?.detailedExplanation,
              suggestedAt: dayjs().toISOString(),
            },
          }));

          trackEventMixpanel('suggest_individual_task_time', {
            status: 'success',
            suggestionKey: key,
            taskId: (task as any)?.taskId ?? (task as any)?.originalTaskId,
          });

          return nextSuggestedTime;
        } catch (e) {
          setTaskSuggestionMap((prev) => ({
            ...prev,
            [key]: {
              ...(prev[key] ?? { status: 'idle' }),
              status: 'failed',
            },
          }));
          trackEventMixpanel('suggest_individual_task_time', {
            status: 'failed',
            suggestionKey: key,
            taskId: (task as any)?.taskId ?? (task as any)?.originalTaskId,
          });
          return null;
        }
      }, taskInfo);
    },
    [
      canRequestSuggestion,
      isPending,
      key,
      parseSuggestedTime,
      resolvedDate,
      resolvedDateString,
      setTaskSuggestionMap,
      setGeneralSnackbar,
      suggestIndividualTaskTime,
      task,
      user.metricId,
      user.preferenceId,
      user.userId,
    ]
  );

  return {
    taskSuggestionMap,
    suggestionKey: key,
    canRequestSuggestion,
    isPending,
    suggestedTime,
    suggestionExplanation,
    requestSuggestion,
    clearSuggestion,
  };
};
